import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ModalSingleInput } from '@/lib/v2/organisms';

import { useFeatureFlag } from '@/src/application/hooks/util/useFeatureFlag';
import { AUTOMATION_PATHS } from '@/src/modules/AutomationModuleNew/constants';
import { useActionsRecurringMessage } from '@/src/modules/AutomationModuleNew/hooks/useActionsRecurringMessage';
import { useRulesValidations } from '@/src/modules/AutomationModuleNew/hooks/useRulesValidations';

import { useDeprecatedBanner } from '@/modules/CampaignsModule/hooks/useDeprecatedBanner';

const TRANSLATE_MODAL_DUPLICATE = 'RECURRENT_MESSAGE.DUPLICATE_RECURRENT';
const TRANSLATE_MODAL = 'RECURRENT_MESSAGE.CREATE_RECURRENT';

type DuplicateRecurringMessageModalProps = {
  isOpen: boolean;
  onClose: () => void;
  campaignID: number;
  nameRecurringMessage: string;
};

const DuplicateRecurringMessageModal = ({
  isOpen,
  onClose,
  campaignID,
  nameRecurringMessage,
}: DuplicateRecurringMessageModalProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { VALIDATION_RULES } = useRulesValidations();
  const { duplicateRecurrent, handleValidation, isLoading, isValidationLoading } =
    useActionsRecurringMessage();
  const { showDeprecatedDragDropForRow } = useDeprecatedBanner();
  const disableLegacyAction = useFeatureFlag('disableLegacyActionDuplication');
  const disabledSubmitButton = disableLegacyAction;

  const handleSubmitDuplicate = useCallback(
    async (name: string) => {
      const campaignIdDuplicate = await duplicateRecurrent(name, campaignID);
      onClose();
      if (campaignIdDuplicate) {
        navigate(
          `/v2/${AUTOMATION_PATHS.MODULE_PATH}/${AUTOMATION_PATHS.RECURRENCES_PATH}/${campaignIdDuplicate}`
        );
      }
    },
    [campaignID, duplicateRecurrent, navigate, onClose]
  );

  let alert = {};

  if (disabledSubmitButton) {
    alert = {
      description: t('CAMPAIGN_ACTIONS_MAIN.DUPLICATE_ACTIONS.disableActions'),
      type: 'warning',
    };
  } else if (!showDeprecatedDragDropForRow) {
    alert = {
      description: t(`${TRANSLATE_MODAL_DUPLICATE}.description`),
      type: 'info',
    };
  } else {
    alert = {
      description: t('CAMPAIGN_ACTIONS_MAIN.DUPLICATE_ACTIONS.deprecated.featured'),
      type: 'warning',
    };
  }

  return (
    <ModalSingleInput
      validateFieldsOnOpen
      alert={alert}
      disabled={isValidationLoading || isLoading}
      disabledSubmitButton={isValidationLoading || isLoading}
      id="duplicate-recurrent-modal"
      isLoading={isLoading || isValidationLoading}
      isOpen={isOpen}
      rules={VALIDATION_RULES}
      title={t(`${TRANSLATE_MODAL_DUPLICATE}.title`)}
      value={`${nameRecurringMessage} ${t(`${TRANSLATE_MODAL}.copy`)}`}
      onClose={onClose}
      onSubmit={handleSubmitDuplicate}
      onValidateInputFetch={handleValidation}
    />
  );
};

export default DuplicateRecurringMessageModal;
